<template>
	<div class="walkthroughs-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Walkthrough Administration</h1>
			<p class="subtitle">Manage and configure interactive walkthroughs for user onboarding</p>
		</div>

		<!-- Alerts -->
		<transition name="fade">
			<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
				<span class="alert-message">{{ alertMessage }}</span>
				<button class="alert-close" @click="showAlert = false">✕</button>
			</div>
		</transition>

		<div class="walkthroughs-container">
			<!-- Walkthroughs List Section -->
			<AdminListSection
				title="Walkthroughs"
				:items="walkthroughs"
				:selected-id="selectedWalkthrough?.id"
				:loading="loading"
				search-placeholder="Search walkthroughs..."
				empty-message="No walkthroughs found"
				:search-fields="['name', 'key']"
				@select="selectWalkthrough"
				@refresh="refreshWalkthroughs"
			>
				<template #item="{ item }">
					<div class="walkthrough-info">
						<div class="walkthrough-title">{{ item.name }}</div>
						<div class="walkthrough-key">{{ item.key }}</div>
					</div>
				</template>
				<!-- <template #item-actions="{ item }">
					<button class="action-btn duplicate-btn" @click="duplicateWalkthrough(item)" title="Duplicate walkthrough">📋</button>
				</template> -->
			</AdminListSection>

			<!-- Walkthrough Details Section -->
			<div v-if="selectedWalkthrough" ref="walkthroughDetailsSection" class="walkthrough-details-section">
				<div class="section-header">
					<h2>{{ selectedWalkthrough.name || 'Walkthrough Details' }}</h2>
					<div class="header-actions-group">
						<button class="action-btn secondary" @click="toggleJsonEditor">
							{{ showJsonEditor ? 'Form View' : 'JSON View' }}
						</button>
						<AdminHeaderActions :loading="loading" :has-id="!!selectedWalkthrough.id" @save="saveWalkthrough" @delete="showDeleteConfirm" @new="newWalkthrough" />
					</div>
				</div>

				<!-- JSON Editor -->
				<div v-if="showJsonEditor" class="json-editor-section">
					<textarea
						v-model="jsonEditorContent"
						class="json-editor-textarea"
						spellcheck="false"
					></textarea>
					<p v-if="jsonError" class="json-error">{{ jsonError }}</p>
				</div>

				<div v-else class="details-grid">
					<!-- Basic Information -->
					<div class="form-section">
						<h3>Walkthrough Details</h3>
						<div class="form-group"><label>Name</label><input type="text" v-model="selectedWalkthrough.name" class="form-input" /></div>
						<div class="form-group"><label>Key</label><input type="text" v-model="selectedWalkthrough.key" class="form-input" /></div>
						<div class="form-group"><label>Type</label><select v-model.number="selectedWalkthrough.type" class="form-input"><option value="0">Sheet</option><option value="1">Story</option></select></div>
						<div class="form-group"><label>Auto-scroll Seconds</label><input type="number" v-model.number="selectedWalkthrough.autoScrollSeconds" class="form-input" /></div>
					</div>

					<!-- Slides Management -->
					<div class="form-section">
						<div class="section-header">
							<h3>Slides</h3>
							<button class="action-btn secondary" @click="addSlide">➕ Add Slide</button>
						</div>
						<div v-if="selectedWalkthrough.slides && selectedWalkthrough.slides.length > 0" class="slides-list">
							<div v-for="(slide, slideIndex) in selectedWalkthrough.slides" :key="slide.id || slideIndex" class="slide-container">
								<div class="slide-item-header" @click="toggleSlide(slideIndex)">
									<h4><span class="expand-icon" :class="{ rotated: expandedSlides.includes(slideIndex) }">›</span><span class="slide-number">{{ slideIndex + 1 }}.</span> {{ slide.title || 'Untitled Slide' }}</h4>
									<div class="header-actions">
										<button class="action-btn" @click.stop="moveSlideUp(slideIndex)" :disabled="slideIndex === 0" title="Move slide up">⬆️</button>
										<button class="action-btn" @click.stop="moveSlideDown(slideIndex)" :disabled="slideIndex === selectedWalkthrough.slides.length - 1" title="Move slide down">⬇️</button>
										<button class="action-btn" @click.stop="duplicateSlide(slideIndex)" title="Duplicate slide">📋</button>
										<button class="action-btn" @click.stop="editSlide(slide, slideIndex)">✏️ Edit</button>
										<button class="action-btn danger" @click.stop="deleteSlide(slideIndex)">🗑️ Delete</button>
									</div>
								</div>
								<transition name="expand">
									<div v-if="expandedSlides.includes(slideIndex)" class="slide-details">
										<div class="slide-detail-row">
											<div class="detail-label">Image:</div>
											<div class="detail-value">{{ slide.imageUri ? '✓ Set' : '✗ Not set' }}</div>
										</div>
										<div class="slide-detail-row">
											<div class="detail-label">Key:</div>
											<div class="detail-value">{{ slide.key || '(empty)' }}</div>
										</div>
										<div class="slide-detail-row">
											<div class="detail-label">Description:</div>
											<div class="detail-value">{{ slide.description || '(empty)' }}</div>
										</div>
										<div class="slide-detail-row">
											<div class="detail-label">Action Text:</div>
											<div class="detail-value">{{ slide.actionText || '(empty)' }}</div>
										</div>
										<div class="slide-detail-row">
											<div class="detail-label">Action Button Colour:</div>
											<div class="detail-value">{{ slide.actionButtonColour || '(empty)' }}</div>
										</div>
									</div>
								</transition>
							</div>
						</div>
						<div v-else class="empty-state-small"><p>No slides defined for this walkthrough.</p></div>
					</div>
				</div>
			</div>
			<div v-else class="empty-state-section"><p>Select a walkthrough to view details</p></div>
		</div>

		<!-- Modals -->
		<transition name="fade">
			<div v-if="deleteConfirm" class="modal-backdrop" @click="deleteConfirm = false">
				<div class="modal-dialog" @click.stop>
					<div class="modal-header"><h3>Confirm Delete</h3><button class="modal-close" @click="deleteConfirm = false">✕</button></div>
					<div class="modal-body"><p>Are you sure you want to delete <strong>{{ selectedWalkthrough.title }}</strong>?</p><p>This action cannot be undone.</p></div>
					<div class="modal-footer"><button class="action-btn secondary" @click="deleteConfirm = false">Cancel</button><button class="action-btn danger" @click="deleteWalkthrough">Delete</button></div>
				</div>
			</div>
		</transition>

		<transition name="fade">
			<div v-if="showSlideModal" class="modal-backdrop" @click="closeSlideModal">
				<div class="modal-dialog" @click.stop>
					<div class="modal-header"><h3>{{ editingSlideIndex === null ? 'Add' : 'Edit' }} Slide</h3><button class="modal-close" @click="closeSlideModal">✕</button></div>
					<div class="modal-body">
						<div class="form-group"><label>Title</label><input type="text" v-model="editingSlide.title" class="form-input" /></div>
						<div class="form-group"><label>Description</label><textarea v-model="editingSlide.description" class="form-textarea" rows="2"></textarea></div>
						<div class="form-group"><label>Sub-description</label><textarea v-model="editingSlide.subDescription" class="form-textarea" rows="2"></textarea></div>
						<div class="form-group"><label>Screen Title</label><input type="text" v-model="editingSlide.screenTitle" class="form-input" /></div>
						<hr class="modal-divider"/>
						<div class="form-group"><label>Action Text</label><input type="text" v-model="editingSlide.actionText" class="form-input" /></div>
						<div class="form-group"><label>Action URI</label><input type="text" v-model="editingSlide.actionUri" class="form-input" /></div>
						<div class="form-group"><label>Action Button Colour</label><input type="text" v-model="editingSlide.actionButtonColour" class="form-input" placeholder="e.g. #25D366" /></div>
						<hr class="modal-divider"/>
						<div class="form-group"><label>Small Text</label><input type="text" v-model="editingSlide.smallText" class="form-input" /></div>
						<div class="form-group"><label>Small Link</label><input type="text" v-model="editingSlide.smallLink" class="form-input" /></div>
						<hr class="modal-divider"/>
						<div class="form-group"><label>Key</label><input type="text" v-model="editingSlide.key" class="form-input" /></div>
						<hr class="modal-divider"/>
						<div class="form-group">
							<label>Image URI</label>
							<div class="input-with-button">
								<input type="text" v-model="editingSlide.imageUri" class="form-input" /><button class="action-btn" type="button" @click="openImageSearch">🖼️</button>
							</div>
							<div v-if="editingSlide.imageUri" class="image-preview-container">
								<video v-if="editingSlide.imageUri.endsWith('.mp4')" :src="editingSlide.imageUri" class="image-preview" muted preload="metadata" />
								<img v-else :src="editingSlide.imageUri" alt="Slide Image Preview" class="image-preview"/>
							</div>
						</div>
						<hr class="modal-divider"/>
						<div class="form-group">
							<label>
								<input type="checkbox" v-model="editingSlide.videoLoop" class="form-checkbox" />
								Video Loop
							</label>
						</div>
						<hr class="modal-divider"/>
					</div>
					<div class="modal-footer"><button class="action-btn secondary" @click="closeSlideModal">Cancel</button><button class="action-btn primary" @click="saveSlide">Save</button></div>
				</div>
			</div>
		</transition>

		<transition name="fade">
			<div v-if="showImageSearchModal" class="modal-backdrop" @click="showImageSearchModal = false">
				<div class="modal-dialog modal-large" @click.stop>
					<div class="modal-header"><h3>Select an Image</h3><button class="modal-close" @click="showImageSearchModal = false">✕</button></div>
					<div class="modal-body">
						<div class="search-box"><input v-model="imageSearchQuery" type="text" placeholder="Search images..." class="search-input" /></div>
						<div v-if="cdnFiles.length > 0" class="file-grid">
							<div v-for="file in filteredCdnFiles" :key="file.fileName" class="file-card" @click="selectImage(file)">
								<div class="file-preview"><img :src="file.uri" :alt="file.fileName" /></div>
								<div class="file-card-info"><div class="file-card-name" :title="file.fileName">{{ file.fileName }}</div></div>
							</div>
						</div>
						<div v-else class="empty-state-small"><p>No images found in CDN.</p></div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';
import AdminListSection from '@/admin/components/AdminListSection.vue';

export default {
	name: 'WalkthroughAdminPage',
	components: {
		AdminHeaderActions,
		AdminListSection
	},
	mixins: [AdminServices],
	data() {
		return {
			loading: false,
			selectedWalkthrough: null,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			deleteConfirm: false,
			expandedSlides: [],
			showSlideModal: false,
			editingSlide: null,
			editingSlideIndex: null,
			showImageSearchModal: false,
			cdnFiles: [],
			imageSearchQuery: '',
			walkthroughs: [],
			showJsonEditor: false,
			jsonEditorContent: '',
			jsonError: ''
		}
	},
	computed: {
		filteredCdnFiles() {
			if (!this.cdnFiles) return [];
			const query = this.imageSearchQuery.toLowerCase();
			return this.cdnFiles.filter(f => f.fileName.toLowerCase().includes(query) && ['jpg', 'jpeg', 'png', 'gif', 'svg'].some(ext => f.fileName.toLowerCase().endsWith(ext)));
		}
	},
	methods: {
		loadWalkthroughs() {
			this.loading = true;
			this._getWalkthroughs((data) => {
				this.loading = false;
				this.walkthroughs = data || [];
				if (data && data.length > 0 && !this.selectedWalkthrough) this.selectWalkthrough(data[0]);
			});
		},
		selectWalkthrough(walkthrough) {
			this.selectedWalkthrough = JSON.parse(JSON.stringify(walkthrough));
			if (!this.selectedWalkthrough.slides) this.selectedWalkthrough.slides = [];
			this.expandedSlides = [];
			this.showJsonEditor = false;
			this.jsonEditorContent = '';
			this.jsonError = '';
			if (window.innerWidth <= 768) this.$nextTick(() => this.$refs.walkthroughDetailsSection?.scrollIntoView({ behavior: 'smooth' }));
		},
		duplicateWalkthrough(walkthrough) {
			const duplicate = JSON.parse(JSON.stringify(walkthrough));
			delete duplicate.id;
			duplicate.name = (duplicate.name || '') + ' (Copy)';
			duplicate.key = (duplicate.key || '') + '_copy';
			if (duplicate.slides) {
				duplicate.slides.forEach(slide => delete slide.id);
			}
			this.loading = true;
			this._createWalkthrough(duplicate,
				(res) => {
					this.showAlertMessage('Walkthrough duplicated successfully', 'success');
					this.loading = false;
					this._getWalkthroughs((data) => {
						this.walkthroughs = data || [];
						const newWalkthrough = this.walkthroughs.find(w => w.id === res.id);
						if (newWalkthrough) this.selectWalkthrough(newWalkthrough);
					});
				},
				() => this.onSaveError('Failed to duplicate walkthrough'));
		},
		newWalkthrough() {
			this.selectedWalkthrough = { title: '', key: '', autoScrollSeconds: 0, slides: [] };
			this.expandedSlides = [];
			this.showJsonEditor = false;
			this.jsonEditorContent = '';
			this.jsonError = '';
		},
		saveWalkthrough() {
			let walkthroughToSave = this.selectedWalkthrough;

			// Apply JSON changes first if in JSON editor mode
			if (this.showJsonEditor) {
				try {
					walkthroughToSave = JSON.parse(this.jsonEditorContent);
					this.jsonError = '';
				} catch (e) {
					this.jsonError = 'Invalid JSON: ' + e.message;
					return;
				}
			}

			if (!walkthroughToSave.name) {
				this.showAlertMessage('Name is required', 'error');
				return;
			}
			if (!walkthroughToSave.key) {
				this.showAlertMessage('Key is required', 'error');
				return;
			}
			this.loading = true;
			if (walkthroughToSave.id) {
				this._updateWalkthrough(walkthroughToSave.id, walkthroughToSave,
					() => {
						this.selectedWalkthrough = walkthroughToSave;
						this.onSaveSuccess();
					},
					() => this.onSaveError('Failed to save walkthrough'));
			} else {
				this._createWalkthrough(walkthroughToSave,
					(res) => this.onSaveSuccess(res),
					() => this.onSaveError('Failed to create walkthrough'));
			}
		},
		toggleJsonEditor() {
			this.jsonError = '';
			if (this.showJsonEditor) {
				// Switching to form view - apply JSON changes
				try {
					const parsed = JSON.parse(this.jsonEditorContent);
					this.selectedWalkthrough = parsed;
					this.showJsonEditor = false;
				} catch (e) {
					this.jsonError = 'Invalid JSON: ' + e.message;
				}
			} else {
				// Switching to JSON view
				this.jsonEditorContent = JSON.stringify(this.selectedWalkthrough, null, 2);
				this.showJsonEditor = true;
			}
		},
		onSaveSuccess(response) {
			this.showAlertMessage('Walkthrough saved successfully', 'success');
			this.loading = false;
			const newId = response ? response.id : this.selectedWalkthrough.id;
			// Reload walkthroughs and re-select the saved one
			this._getWalkthroughs((data) => {
				this.walkthroughs = data || [];
				this.$nextTick(() => {
					const saved = this.walkthroughs.find(w => w.id === newId);
					if (saved) this.selectWalkthrough(saved);
				});
			});
		},
		onSaveError(message) {
			this.showAlertMessage(message, 'error');
			this.loading = false;
		},
		showDeleteConfirm() {
			if (this.selectedWalkthrough?.id) this.deleteConfirm = true;
		},
		deleteWalkthrough() {
			this.deleteConfirm = false;
			this.loading = true;
			this._deleteWalkthrough(this.selectedWalkthrough.id,
				() => {
					this.showAlertMessage('Walkthrough deleted', 'success');
					this.selectedWalkthrough = null;
					this.loading = false;
					this.refreshWalkthroughs();
				},
				() => this.onSaveError('Failed to delete walkthrough'));
		},
		refreshWalkthroughs() {
			this.loadWalkthroughs();
		},
		showAlertMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			setTimeout(() => { this.showAlert = false; }, 4000);
		},
		toggleSlide(index) {
			const idx = this.expandedSlides.indexOf(index);
			if (idx > -1) this.expandedSlides.splice(idx, 1);
			else this.expandedSlides.push(index);
		},
		addSlide() {
			const nextOrder = this.selectedWalkthrough.slides ? this.selectedWalkthrough.slides.length : 0;
			this.editingSlide = {
				order: nextOrder,
				title: '',
				description: '',
				subDescription: '',
				screenTitle: '',
				actionText: '',
				actionUri: '',
				actionButtonColour: '',
				smallText: '',
				smallLink: '',
				imageUri: '',
				key: '',
				videoLoop: false
			};
			this.editingSlideIndex = null;
			this.showSlideModal = true;
		},
		editSlide(slide, index) {
			this.editingSlide = { ...slide };
			this.editingSlideIndex = index;
			this.showSlideModal = true;
		},
		saveSlide() {
			if (!this.editingSlide.title) {
				this.showAlertMessage('Slide title is required', 'error');
				return;
			}
			// Sanitise text fields to remove Unicode line/paragraph separators
			const textFields = ['title', 'description', 'subDescription', 'screenTitle', 'actionText', 'smallText'];
			textFields.forEach(field => {
				if (this.editingSlide[field]) {
					this.editingSlide[field] = this.editingSlide[field].replace(/[\u2028\u2029]/g, ' ');
				}
			});
			if (this.editingSlideIndex !== null) {
				this.selectedWalkthrough.slides.splice(this.editingSlideIndex, 1, this.editingSlide);
			} else {
				this.selectedWalkthrough.slides.push(this.editingSlide);
			}
			this.updateSlideOrders();
			this.closeSlideModal();
		},
		deleteSlide(index) {
			this.selectedWalkthrough.slides.splice(index, 1);
			this.updateSlideOrders();
		},
		duplicateSlide(index) {
			const slide = this.selectedWalkthrough.slides[index];
			const duplicatedSlide = JSON.parse(JSON.stringify(slide));
			delete duplicatedSlide.id;
			this.selectedWalkthrough.slides.splice(index + 1, 0, duplicatedSlide);
			this.updateSlideOrders();
		},
		moveSlideUp(index) {
			if (index > 0) {
				const temp = this.selectedWalkthrough.slides[index];
				this.selectedWalkthrough.slides[index] = this.selectedWalkthrough.slides[index - 1];
				this.selectedWalkthrough.slides[index - 1] = temp;
				this.updateSlideOrders();
				this.expandedSlides = [];
			}
		},
		moveSlideDown(index) {
			if (index < this.selectedWalkthrough.slides.length - 1) {
				const temp = this.selectedWalkthrough.slides[index];
				this.selectedWalkthrough.slides[index] = this.selectedWalkthrough.slides[index + 1];
				this.selectedWalkthrough.slides[index + 1] = temp;
				this.updateSlideOrders();
				this.expandedSlides = [];
			}
		},
		updateSlideOrders() {
			if (this.selectedWalkthrough.slides) {
				this.selectedWalkthrough.slides.forEach((slide, index) => {
					slide.order = index;
				});
			}
		},
		closeSlideModal() {
			this.showSlideModal = false;
			this.editingSlide = null;
			this.editingSlideIndex = null;
		},
		openImageSearch() {
			this.showImageSearchModal = true;
			this.loadCdnFiles();
		},
		loadCdnFiles() {
			this._getFiles(files => {
				this.cdnFiles = files || [];
			});
		},
		selectImage(file) {
			if (!this.editingSlide) return;
			this.editingSlide.imageUri = file.uri;
			this.showImageSearchModal = false;
		}
	},
	mounted() {
		this.loadWalkthroughs();
	}
}
</script>

<style scoped>
/* Page and container styles */
.walkthroughs-page {
	min-height: 100%;
	padding: 15px;
	overflow-x: hidden;
}

.page-header {
	margin-bottom: 20px;
}

.page-header h1 {
	margin: 0 0 5px 0;
	color: var(--admin-text-primary);
	font-size: 24px;
	font-weight: 600;
}

.subtitle {
	margin: 0;
	color: var(--admin-text-secondary);
	font-size: 14px;
}

.alert {
	margin-bottom: 20px;
	padding: 12px 15px;
	border-radius: var(--border-radius-s);
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background-color: rgba(76, 175, 80, 0.1);
	color: #2e7d32;
	border: 1px solid rgba(76, 175, 80, 0.3);
}

.alert-error {
	background-color: rgba(211, 47, 47, 0.1);
	color: #c62828;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.alert-close {
	background: none;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 16px;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.walkthroughs-container {
	display: grid;
	grid-template-columns: 300px 1fr;
	gap: 15px;
}

.walkthroughs-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 12px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	min-height: 600px;
	max-height: 80vh;
	min-width: 0;
}

.section-header {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 12px;
	gap: 8px;
}

.header-actions-group {
	display: flex;
	gap: 8px;
	align-items: center;
}

/* JSON Editor */
.json-editor-section {
	background: var(--admin-hover-bg);
	border-radius: 8px;
	padding: 16px;
	margin-top: 12px;
}

.json-editor-textarea {
	width: 100%;
	min-height: 500px;
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: 4px;
	background: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: monospace;
	resize: vertical;
	white-space: pre;
	overflow-x: auto;
}

.json-error {
	color: #f44336;
	font-size: 13px;
	margin: 8px 0 0 0;
}

.section-header h2,
.section-header h3 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.search-box {
	margin-bottom: 15px;
}

.search-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-primary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
}

.spinner {
	border: 3px solid var(--admin-border);
	border-top: 3px solid var(--admin-text-primary);
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin-bottom: 10px;
}

@keyframes spin {
	0% {
		transform: rotate(0);
	}
	100% {
		transform: rotate(360deg);
	}
}

.walkthroughs-list {
	flex: 1;
	overflow-y: auto;
}

.walkthrough-items {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.walkthrough-item {
	padding: 12px;
	background-color: var(--admin-bg-secondary);
	color: var(--admin-text-primary);
	cursor: pointer;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
	font-weight: 400;
}

.walkthrough-item:hover {
	background-color: var(--admin-hover-bg);
}

.walkthrough-item.active {
	background-color: var(--admin-active-bg);
	border-left-color: var(--background-color-primary);
	font-weight: 500;
}

.walkthrough-item-content {
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.duplicate-btn {
	padding: 4px 8px;
	font-size: 14px;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.walkthrough-item:hover .duplicate-btn {
	opacity: 1;
}

.walkthrough-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.walkthrough-title {
	color: var(--admin-text-primary);
	font-weight: 600;
	font-size: 13px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.walkthrough-key {
	color: var(--admin-text-secondary);
	font-size: 12px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.empty-state {
	text-align: center;
	padding: 40px 20px;
	color: var(--admin-text-secondary);
}

.walkthrough-details-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 12px;
	box-shadow: var(--box-shadow);
	max-height: 80vh;
	overflow-y: auto;
	min-width: 0;
}

.empty-state-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 40px 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	align-items: center;
	justify-content: center;
	color: var(--admin-text-secondary);
	text-align: center;
	min-height: 400px;
}

.details-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 20px;
}

.form-section {
	background-color: var(--admin-bg-primary);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	padding: 10px;
	margin-top: 10px;
}

.form-section:first-child {
	margin-top: 0;
}

.form-section h3 {
	margin: 0 0 15px 0;
	color: var(--admin-text-primary);
	font-size: 16px;
	font-weight: 600;
	padding-bottom: 10px;
	border-bottom: 1px solid var(--admin-border);
}

.form-group {
	margin-bottom: 15px;
}

.form-group:last-child {
	margin-bottom: 0;
}

.form-group label {
	display: block;
	margin-bottom: 6px;
	color: var(--admin-text-primary);
	font-size: 12px;
	font-weight: 600;
}

.form-input,
.form-textarea {
	width: 100%;
	padding: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	font-size: 14px;
	font-family: inherit;
}

.form-input:focus,
.form-textarea:focus {
	outline: none;
	border-color: var(--admin-text-primary);
}

.form-textarea {
	resize: vertical;
	min-height: 60px;
}

.input-with-button {
	display: flex;
	gap: 10px;
}

.input-with-button .form-input {
	flex: 1;
}

.input-with-button .action-btn {
	padding: 8px 12px;
	font-size: 16px;
}

.modal-divider {
	border: none;
	border-top: 1px solid var(--admin-border);
	margin: 20px 0;
}

.action-btn {
	padding: 8px 14px;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.action-btn.primary {
	background-color: var(--background-color-alt6);
	color: #fff;
}

.action-btn.secondary {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn.danger {
	background-color: rgba(211, 47, 47, 0.1);
	color: #d32f2f;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-dialog {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	box-shadow: var(--box-shadow);
	max-width: 600px;
	width: 90%;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
}

.modal-dialog.modal-large {
	max-width: 900px;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 20px;
	border-bottom: 1px solid var(--admin-border);
}

.modal-header h3 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
}

.modal-close {
	background: none;
	border: none;
	color: var(--admin-text-primary);
	font-size: 24px;
	cursor: pointer;
	padding: 0;
	width: 32px;
	height: 32px;
}

.modal-body {
	padding: 20px;
	color: var(--admin-text-primary);
	overflow-y: auto;
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 20px;
	border-top: 1px solid var(--admin-border);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.slides-list {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-top: 15px;
}

.slide-container {
	background-color: var(--admin-hover-bg);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
}

.slide-item-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px;
	cursor: pointer;
}

.slide-item-header h4 {
	margin: 0;
	font-size: 14px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.expand-icon {
	font-size: 18px;
	transition: transform 0.3s ease;
}

.expand-icon.rotated {
	transform: rotate(90deg);
}

.slide-number {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 24px;
	padding: 0 6px;
	background-color: var(--admin-border);
	color: var(--admin-text-primary);
	border-radius: 4px;
	font-weight: 600;
	font-size: 12px;
}

.slide-details {
	padding: 0 12px 12px 12px;
}

.slide-detail-row {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding: 8px;
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-s);
	margin-bottom: 8px;
	font-size: 13px;
}

.slide-detail-label {
	font-weight: 600;
	color: var(--admin-text-primary);
}

.slide-detail-value {
	color: var(--admin-text-secondary);
	word-break: break-word;
}

.empty-state-small {
	text-align: center;
	color: var(--admin-text-secondary);
	font-size: 12px;
	padding: 15px;
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease-in-out;
	overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
	max-height: 1000px;
	opacity: 1;
}

.image-preview-container {
	margin-top: 10px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	padding: 5px;
	background-color: var(--admin-bg-secondary);
	display: flex;
	justify-content: center;
	align-items: center;
	max-width: 150px;
	max-height: 150px;
}

.image-preview {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.file-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	gap: 15px;
}

.file-card {
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	overflow: hidden;
	transition: all 0.2s ease;
	cursor: pointer;
}

.file-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.file-preview {
	width: 100%;
	height: 100px;
	background-color: var(--admin-bg-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.file-preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.file-card-info {
	padding: 10px;
}

.file-card-name {
	font-weight: 500;
	font-size: 12px;
	word-break: break-word;
}

.detail-label {
	font-weight: 600;
	color: var(--admin-text-primary);
}

.detail-value {
	color: var(--admin-text-secondary);
	word-break: break-word;
}

@media (max-width: 1024px) {
	.walkthroughs-container {
		grid-template-columns: minmax(250px, 300px) 1fr;
	}
}

@media (max-width: 768px) {
	.walkthroughs-page {
		padding: 12px;
	}

	.walkthroughs-container {
		grid-template-columns: 1fr;
		gap: 12px;
	}

	.walkthroughs-list-section {
		min-height: auto;
		max-height: none;
		height: auto;
		margin-bottom: 15px;
		padding: 15px;
	}

	.walkthrough-details-section {
		padding: 15px;
	}
}

:root.light-mode .walkthroughs-list-section,
:root.light-mode .walkthrough-details-section,
:root.light-mode .empty-state-section,
:root.light-mode .modal-dialog,
:root.light-mode .slide-container {
	background-color: #ffffff !important;
	border-color: #e0e0e0 !important;
}

:root.light-mode .form-section {
	background-color: #f9f9f9 !important;
	border-color: #e0e0e0 !important;
}

:root.light-mode .walkthrough-item.active {
	background-color: #f0f0f0 !important;
	border-color: var(--background-color-alt6) !important;
}
</style>
